// src/pages/admin/Analytics.jsx
import React, { useState, useEffect } from 'react';
import { Shield, Info, Download, AlertCircle } from 'lucide-react';
import axios from 'axios';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
 
  ResponsiveContainer
} from 'recharts';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/ui/Alert';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';


const COLORS = {
  primary: '#4F46E5',
  secondary: '#10B981',
  tertiary: '#F59E0B',
  quaternary: '#EF4444',
  blue: '#3B82F6',
  green: '#10B981',
  purple: '#8B5CF6',
  yellow: '#F59E0B',
  red: '#EF4444'
};

const Analytics = () => {
 
  const {
    canViewAnalytics,
    canGenerateReports,
    isSuperAdmin,
    isAdmin
  } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [period, setPeriod] = useState('7d');
  const [analytics, setAnalytics] = useState({
    userGrowth: [],
    consultationTrends: [],
    orderTrends: [],
    revenueTrends: [],
    summary: {
      totalUsers: 0,
      totalConsultations: 0,
      totalOrders: 0,
      totalRevenue: 0
    }
  });

  useEffect(() => {
    if (canViewAnalytics) {
      fetchAnalytics();
    }
  }, [period, canViewAnalytics]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Not authenticated. Please login again.');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/admin/dashboard/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      
      
      const data = response.data.data || response.data;
      
      setAnalytics({
        userGrowth: data.userGrowth || generateMockData('users'),
        consultationTrends: data.consultationTrends || generateMockData('consultations'),
        orderTrends: data.orderTrends || generateMockData('orders'),
        revenueTrends: data.revenueTrends || generateMockData('revenue'),
        summary: data.summary || {
          totalUsers: 150,
          totalConsultations: 89,
          totalOrders: 134,
          totalRevenue: 45000
        }
      });
      
    } catch (err) {
      console.error('Analytics error:', err);
      
    
      if (err.response?.status === 404 || err.response?.status === 500) {
        console.warn('⚠️ Using mock data - backend analytics endpoint may not be implemented');
        setAnalytics({
          userGrowth: generateMockData('users'),
          consultationTrends: generateMockData('consultations'),
          orderTrends: generateMockData('orders'),
          revenueTrends: generateMockData('revenue'),
          summary: {
            totalUsers: 150,
            totalConsultations: 89,
            totalOrders: 134,
            totalRevenue: 45000
          }
        });
        setError('Using sample data - backend analytics endpoint may not be implemented');
      } else {
        setError(err.response?.data?.message || 'Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

 
  const generateMockData = (type) => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      let value;
      switch(type) {
        case 'users':
          value = Math.floor(Math.random() * 20) + 10;
          break;
        case 'consultations':
          value = Math.floor(Math.random() * 15) + 5;
          break;
        case 'orders':
          value = Math.floor(Math.random() * 25) + 10;
          break;
        case 'revenue':
          value = Math.floor(Math.random() * 5000) + 2000;
          break;
        default:
          value = Math.floor(Math.random() * 20);
      }
      
      data.push({
        date: dateStr,
        _id: dateStr,
        count: type !== 'revenue' ? value : undefined,
        revenue: type === 'revenue' ? value : undefined,
        value: value
      });
    }
    
    return data;
  };

  const exportAnalytics = () => {
    const dataStr = JSON.stringify(analytics, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-${period}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSuccess('Analytics data exported successfully!');
  };

  const periodOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  
  const totalUsers = analytics.userGrowth.reduce((sum, item) => sum + (item.count || item.value || 0), 0);
  const totalConsultations = analytics.consultationTrends.reduce((sum, item) => sum + (item.count || item.value || 0), 0);
  const totalOrders = analytics.orderTrends.reduce((sum, item) => sum + (item.count || item.value || 0), 0);
  const totalRevenue = analytics.revenueTrends.reduce((sum, item) => sum + (item.revenue || item.value || 0), 0);

  
  if (!canViewAnalytics) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view analytics.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            Analytics
            {isSuperAdmin && <Shield className="w-7 h-7 text-purple-600" />}
          </h1>
          <p className="text-gray-600 mt-1">Track system performance and growth</p>
        </div>
        
        {/* Period Selector */}
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          {periodOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Permission Badge */}
      {isSuperAdmin ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Super Admin Privileges Active</p>
            <p className="text-purple-700 text-sm">
              Full analytics access with export and reporting capabilities
            </p>
          </div>
        </div>
      ) : isAdmin ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Admin Access</p>
            <p className="text-blue-700 text-sm">
              You can view all analytics and generate reports
            </p>
          </div>
        </div>
      ) : null}

      {/* Alerts */}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">User Growth</p>
              <p className="text-3xl font-bold mt-2">{totalUsers}</p>
              <p className="text-blue-100 text-xs mt-2">Total in period</p>
            </div>
            <div className="w-12 h-12 bg-blue-400 bg-opacity-30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Consultations</p>
              <p className="text-3xl font-bold mt-2">{totalConsultations}</p>
              <p className="text-green-100 text-xs mt-2">Total in period</p>
            </div>
            <div className="w-12 h-12 bg-green-400 bg-opacity-30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Orders</p>
              <p className="text-3xl font-bold mt-2">{totalOrders}</p>
              <p className="text-purple-100 text-xs mt-2">Total in period</p>
            </div>
            <div className="w-12 h-12 bg-purple-400 bg-opacity-30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Revenue</p>
              <p className="text-3xl font-bold mt-2">KES {totalRevenue.toLocaleString()}</p>
              <p className="text-yellow-100 text-xs mt-2">Total in period</p>
            </div>
            <div className="w-12 h-12 bg-yellow-400 bg-opacity-30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Export Button - Both admins can export */}
      {canGenerateReports && (
        <div className="flex justify-end">
          <button
            onClick={exportAnalytics}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-colors"
          >
            <Download className="w-5 h-5" />
            Export Analytics Data
          </button>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart - Line Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analytics.userGrowth}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke={COLORS.blue} 
                fillOpacity={1} 
                fill="url(#colorUsers)"
                name="New Users"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Consultation Trends - Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultation Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.consultationTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill={COLORS.green}
                radius={[8, 8, 0, 0]}
                name="Consultations"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Order Trends - Line Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.orderTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={COLORS.purple} 
                strokeWidth={3}
                dot={{ fill: COLORS.purple, r: 4 }}
                activeDot={{ r: 6 }}
                name="Orders"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trends - Area Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analytics.revenueTrends}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.yellow} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={COLORS.yellow} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#888"
                tickFormatter={(value) => `KES ${(value/1000).toFixed(0)}k`}
              />
              <Tooltip 
                content={<CustomTooltip />}
                formatter={(value) => `KES ${value.toLocaleString()}`}
              />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke={COLORS.yellow} 
                fillOpacity={1} 
                fill="url(#colorRevenue)"
                name="Revenue"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Period Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{totalUsers}</div>
            <div className="text-sm text-gray-600 mt-1">New Users</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{totalConsultations}</div>
            <div className="text-sm text-gray-600 mt-1">Total Consultations</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{totalOrders}</div>
            <div className="text-sm text-gray-600 mt-1">Total Orders</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">KES {totalRevenue.toLocaleString()}</div>
            <div className="text-sm text-gray-600 mt-1">Total Revenue</div>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-900 font-medium">Analytics Information</p>
            <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
              <li>Data updates in real-time based on system activity</li>
              <li>Use the period selector to view different time ranges</li>
              <li>Export data to JSON for further analysis</li>
              <li>Charts show trends and patterns in your data</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;